import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { adminEvents, type RadioEvent, eventTypeLabels, eventStatusColors } from "@/mocks/adminEvents";
import { radioStations } from "@/mocks/radios";

function formatDateSpanish(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString("es-ES", { weekday: "long", day: "numeric", month: "long", year: "numeric" });
}

function formatCount(n: number): string {
  if (n >= 1000) return `${(n / 1000).toFixed(1)}K`;
  return n.toString();
}

export default function TvEventDetailPage() {
  const navigate = useNavigate();
  const { eventId } = useParams<{ eventId: string }>();
  const [event, setEvent] = useState<RadioEvent | null>(null);
  const [stationName, setStationName] = useState<string>("");
  const [focusedBtn, setFocusedBtn] = useState<string>("back");
  const [showQr, setShowQr] = useState(false);

  const buttons = ["back", "radio", "qr"] as const;

  useEffect(() => {
    if (!eventId) return;
    const numId = Number(eventId);
    if (isNaN(numId)) return;
    const evt = adminEvents.find((e) => e.id === numId);
    if (!evt) return;
    setEvent(evt);
    const radio = radioStations.find((r) => r.id === evt.stationId);
    if (radio) setStationName(radio.name);
  }, [eventId]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowRight") {
        e.preventDefault();
        const idx = buttons.indexOf(focusedBtn as typeof buttons[number]);
        setFocusedBtn(buttons[(idx + 1) % buttons.length]);
      } else if (e.key === "ArrowLeft") {
        e.preventDefault();
        const idx = buttons.indexOf(focusedBtn as typeof buttons[number]);
        setFocusedBtn(buttons[(idx - 1 + buttons.length) % buttons.length]);
      } else if (e.key === "Escape" || e.key === "Backspace") {
        e.preventDefault();
        if (showQr) { setShowQr(false); return; }
        navigate("/tv-app");
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (focusedBtn === "back") navigate("/tv-app");
        else if (focusedBtn === "radio" && event) navigate(`/tv-app/radio/${event.stationId}`);
        else if (focusedBtn === "qr") setShowQr(true);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [focusedBtn, navigate, event, showQr]);

  if (!event) {
    return (
      <div className="min-h-screen bg-[#0a0a0f] text-white flex items-center justify-center select-none">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto rounded-2xl bg-white/5 flex items-center justify-center mb-6"><i className="ri-calendar-event-line text-white/40 text-3xl"></i></div>
          <h1 className="text-2xl font-bold text-white mb-3">Evento no encontrado</h1>
          <button onClick={() => navigate("/tv-app")} className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold cursor-pointer">Volver</button>
        </div>
      </div>
    );
  }

  const statusInfo = eventStatusColors[event.status];
  const typeLabel = eventTypeLabels[event.type];

  return (
    <div className="min-h-screen bg-[#0a0a0f] text-white select-none overflow-x-hidden">
      <div className="relative h-[350px] lg:h-[440px] overflow-hidden">
        <img src={event.image} alt="" className="w-full h-full object-cover opacity-30 blur-sm scale-105" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0a0a0f] via-[#0a0a0f]/50 to-[#0a0a0f]/30"></div>
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between px-6 lg:px-10 py-5 z-10">
          <button onClick={() => navigate("/tv-app")} className={`flex items-center gap-2 px-5 py-3 rounded-xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-semibold ${focusedBtn === "back" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30" : "bg-white/10 hover:bg-white/15 text-white"}`}>
            <i className="ri-arrow-left-line text-lg"></i>Volver
          </button>
          <div className="flex items-center gap-3">
            <span className={`px-3 py-1.5 rounded-full text-xs font-bold ${statusInfo.text} ${statusInfo.bg}`}>{event.status === "publicado" ? "Publicado" : event.status === "finalizado" ? "Finalizado" : event.status === "cancelado" ? "Cancelado" : "Borrador"}</span>
            <span className="px-3 py-1.5 rounded-full bg-amber-500/90 text-white text-xs font-bold">{typeLabel}</span>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 right-0 z-10 px-6 lg:px-10 pb-8">
          <h1 className="text-3xl lg:text-4xl font-bold text-white">{event.title}</h1>
          <div className="flex items-center gap-4 mt-3 flex-wrap">
            <span className="text-lg text-white/60"><i className="ri-calendar-line mr-1.5"></i>{event.date} · {event.time}</span>
            <span className="text-lg text-white/60"><i className="ri-map-pin-line mr-1.5"></i>{event.location}</span>
            {stationName && <span className="text-lg text-white/60">{stationName}</span>}
          </div>
        </div>
      </div>

      <div className="px-6 lg:px-10 py-8 space-y-8">
        <div className="flex items-center gap-10 flex-wrap">
          <div><p className="text-3xl font-bold text-white">{formatCount(event.participants)}</p><p className="text-sm text-white/50 mt-1">Participantes</p></div>
          <div className="w-px h-10 bg-white/10"></div>
          <div><p className="text-3xl font-bold text-accent-400">{formatCount(event.interested)}</p><p className="text-sm text-white/50 mt-1">Interesados</p></div>
          <div className="w-px h-10 bg-white/10"></div>
          <div><p className="text-3xl font-bold text-accent-400">{event.capacity}</p><p className="text-sm text-white/50 mt-1">Capacidad</p></div>
        </div>

        <div className="max-w-3xl">
          <p className="text-base text-white/60 leading-relaxed">{event.description}</p>
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          <span className="px-3 py-1.5 rounded-full bg-white/5 text-white/50 text-sm font-semibold flex items-center gap-1.5"><i className="ri-global-line"></i>{event.modality === "presencial" ? "Presencial" : event.modality === "online" ? "Online" : "Híbrido"}</span>
          <span className="px-3 py-1.5 rounded-full bg-white/5 text-white/50 text-sm font-semibold flex items-center gap-1.5"><i className="ri-share-forward-line"></i>{formatCount(event.shares)} compartidos</span>
        </div>

        <div className="flex items-center justify-center gap-3 pt-4 pb-8">
          <button onClick={() => navigate("/tv-app")} className={`flex items-center gap-2 px-5 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-bold ${focusedBtn === "back" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105" : "bg-white/10 hover:bg-white/15 text-white"}`}><i className="ri-arrow-left-line text-lg"></i>Volver</button>
          <button onClick={() => event && navigate(`/tv-app/radio/${event.stationId}`)} className={`flex items-center gap-2 px-5 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-bold ${focusedBtn === "radio" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105" : "bg-white/10 hover:bg-white/15 text-white"}`}><i className="ri-radio-line text-lg"></i>Ver emisora</button>
          <button onClick={() => setShowQr(true)} className={`flex items-center gap-2 px-5 py-4 rounded-2xl transition-all cursor-pointer whitespace-nowrap outline-none text-base font-bold ${focusedBtn === "qr" ? "bg-white text-[#0a0a0f] ring-4 ring-white/30 scale-105" : "bg-white/10 hover:bg-white/15 text-white"}`}><i className="ri-qr-code-line text-lg"></i>Participar desde el celu</button>
        </div>

        {showQr && (
          <div className="fixed inset-0 z-50 bg-[#0a0a0f]/90 backdrop-blur-sm flex items-center justify-center" onClick={() => setShowQr(false)}>
            <div className="bg-[#0f0f15] border border-white/10 rounded-3xl p-8 max-w-md w-full mx-4 text-center" onClick={(e) => e.stopPropagation()}>
              <div className="w-48 h-48 mx-auto rounded-2xl bg-white p-3 mb-6"><img src={`https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(`https://radiofrecuenciaweb.com/events`)}`} alt="QR" className="w-full h-full object-contain rounded-xl" /></div>
              <h3 className="text-xl font-bold text-white mb-2">Participar desde el celular</h3>
              <p className="text-sm text-white/50 mb-2">Escaneá el QR para ver este evento y participar desde tu celular.</p>
              <p className="text-sm text-white/30 font-mono mb-6">radiofrecuenciaweb.com/events</p>
              <button onClick={() => setShowQr(false)} className="px-6 py-3 rounded-xl bg-white/10 hover:bg-white/15 text-white font-semibold cursor-pointer">Cerrar</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}